import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button, Input, QRCode, message, Spin } from 'antd';
import { Camera, RefreshCw, CheckCircle2, AlertTriangle, LifeBuoy, Lock } from 'lucide-react';
import {
  verifyEkycDocument,
  checkEkycLiveness,
  getEkycStatus,
  EkycOcrResult,
  EkycStatus,
} from '../../api/user/ekyc.api';
import { getConsentStatus } from '../../api/user/consent.api';
import PolicyConsentModal from '../../components/policy/policy-consent-modal';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import {
  DocumentImages,
  attemptsLeftNotice,
  ekycErrorMessage,
  finishBlocker,
  isDocumentRetakeError,
  isLivenessVerified,
  isEkycLocked,
  needsDocumentRead,
} from './ekyc-flow';

const BRAND = '#066BFF';

type Step = 'intro' | 'front' | 'back' | 'face' | 'review';

interface BankInfo {
  bankName: string;
  bankAccountNumber: string;
  bankAccountName: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Chỉ gọi khi bước khuôn mặt đã đạt (máy chủ trả isVerified=true). Trả OCR + thông tin ngân hàng để luồng rút dùng. */
  onVerified: (data: { ocr: EkycOcrResult; bank: BankInfo }) => void;
  /** Chỉ xác thực DANH TÍNH (không thu thông tin ngân hàng) — dùng cho luồng đăng ký Content Creator. */
  identityOnly?: boolean;
}

/** Một bước chụp bằng camera: hiện video + khung định vị + nút chụp. */
const CameraCapture: React.FC<{
  facingMode: 'environment' | 'user';
  shape: 'card' | 'face';
  hint: string;
  onCapture: (blob: Blob, preview: string) => void;
  onNoCamera: () => void;
}> = ({ facingMode, shape, hint, onCapture, onNoCamera }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch {
        onNoCamera();
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [facingMode, onNoCamera]);

  const snap = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext('2d')?.drawImage(v, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (blob) onCapture(blob, URL.createObjectURL(blob));
      },
      'image/jpeg',
      0.92
    );
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative w-full max-w-[420px] bg-black rounded-[12px] overflow-hidden">
        <video ref={videoRef} playsInline muted className="w-full h-auto" />
        {/* khung định vị */}
        <div
          className={`absolute inset-0 m-auto border-2 border-white/80 pointer-events-none ${
            shape === 'card'
              ? 'w-[85%] h-[60%] rounded-[12px] top-0 bottom-0'
              : 'w-[55%] aspect-square rounded-full'
          }`}
          style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)' }}
        />
      </div>
      <p className="text-[14px] text-slate-500 text-center">{hint}</p>
      <Button
        type="primary"
        size="large"
        icon={<Camera size={18} />}
        disabled={!ready}
        className="!rounded-full"
        style={{ background: BRAND, borderColor: BRAND }}
        onClick={snap}
      >
        Chụp
      </Button>
    </div>
  );
};

const TOO_MANY_FAILURES_TEXT =
  'Bạn đã thử xác minh danh tính quá số lần cho phép. Vui lòng nhắn bộ phận hỗ trợ để được mở lại.';
const DOCUMENT_FALLBACK_TEXT = 'Cab chưa đọc được giấy tờ. Bạn hãy chụp lại căn cước rõ nét, đủ 4 góc.';
const FACE_FAILED_TEXT =
  'Khuôn mặt chưa khớp với ảnh trên giấy tờ hoặc ảnh chưa rõ. Bạn hãy chụp lại khuôn mặt ở nơi đủ sáng và nhìn thẳng.';
const FACE_FALLBACK_TEXT = 'Cab chưa kiểm tra được khuôn mặt lúc này. Bạn hãy thử chụp lại khuôn mặt.';

/** Hết lượt thử: thay cho màn chụp, chỉ đường nhắn hỗ trợ (lấy email/link hỗ trợ từ cài đặt công khai). */
const EkycLockedNotice: React.FC = () => {
  const { get } = useFeatureFlags();
  const supportEmail = get('support.email').trim();
  const supportLink = get('support.link').trim();
  return (
    <div className="flex flex-col items-center gap-3 py-4 text-center">
      <Lock className="text-amber-500" size={28} />
      <p className="text-[15px] font-medium">{TOO_MANY_FAILURES_TEXT}</p>
      {supportEmail || supportLink ? (
        <div className="flex flex-wrap items-center justify-center gap-3 rounded-lg bg-blue-50 dark:bg-slate-800 px-3 py-2 text-sm">
          <LifeBuoy size={16} className="text-blue-600" />
          <span className="text-gray-600 dark:text-slate-300">Bạn cần hỗ trợ?</span>
          {supportEmail && (
            <a href={`mailto:${supportEmail}`} className="text-blue-600 hover:underline">
              {supportEmail}
            </a>
          )}
          {supportLink && (
            <a href={supportLink} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
              Liên hệ hỗ trợ
            </a>
          )}
        </div>
      ) : (
        <p className="text-[13px] text-slate-500">
          Bạn hãy nhắn bộ phận hỗ trợ của Cab, đội ngũ sẽ kiểm tra và mở lại lượt cho bạn.
        </p>
      )}
    </div>
  );
};

type Img = { blob: Blob; url: string };

export const EkycCameraModal: React.FC<Props> = ({ open, onClose, onVerified, identityOnly }) => {
  const [step, setStep] = useState<Step>('intro');
  const [noCamera, setNoCamera] = useState(false);
  const [front, setFront] = useState<Img | null>(null);
  const [back, setBack] = useState<Img | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [ocr, setOcr] = useState<EkycOcrResult | null>(null);
  // Bộ ảnh giấy tờ đã đọc thành công: chụp lại riêng khuôn mặt thì không gọi đọc giấy tờ lần nữa.
  const [lastRead, setLastRead] = useState<DocumentImages | null>(null);
  const [faceVerified, setFaceVerified] = useState(false);
  const [docError, setDocError] = useState<string | null>(null);
  const [faceError, setFaceError] = useState<string | null>(null);
  const [status, setStatus] = useState<EkycStatus | null>(null);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [dataConsent, setDataConsent] = useState<boolean | null>(null); // đồng ý điều khoản dữ liệu (NĐ 13/2023)
  const [bank, setBank] = useState<BankInfo>({
    bankName: '',
    bankAccountNumber: '',
    bankAccountName: '',
  });

  const refreshStatus = useCallback(async () => {
    try {
      const s = await getEkycStatus();
      setStatus(s);
    } catch {
      // Không đọc được trạng thái thì vẫn cho chụp; máy chủ tự chặn khi hết lượt.
    } finally {
      setStatusLoaded(true);
    }
  }, []);

  // Khi mở modal: kiểm tra đã đồng ý điều khoản Định danh & Dữ liệu cá nhân chưa, và còn lượt thử không.
  useEffect(() => {
    if (!open) return;
    getConsentStatus(['ekyc-data'])
      .then((list) => setDataConsent(!!list.find((x) => x.key === 'ekyc-data')?.accepted))
      .catch(() => setDataConsent(false));
    refreshStatus();
  }, [open, refreshStatus]);

  const reset = useCallback(() => {
    setStep('intro');
    setNoCamera(false);
    setFront(null);
    setBack(null);
    setOcr(null);
    setLastRead(null);
    setFaceVerified(false);
    setDocError(null);
    setFaceError(null);
    setStatus(null);
    setStatusLoaded(false);
    setSubmitting(false);
    setBank({ bankName: '', bankAccountNumber: '', bankAccountName: '' });
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  // Chụp xong khuôn mặt: đọc giấy tờ nếu cần, rồi kiểm tra khuôn mặt.
  const runEkyc = async (f: Blob, b: Blob | null, fc: Blob) => {
    setStep('review');
    setSubmitting(true);
    setFaceVerified(false);
    setDocError(null);
    setFaceError(null);
    let failed = false;
    try {
      if (needsDocumentRead(lastRead, ocr, f, b)) {
        try {
          const ocrRes = await verifyEkycDocument(f, b);
          if (!ocrRes?.name) {
            setOcr(null);
            setLastRead(null);
            setDocError(DOCUMENT_FALLBACK_TEXT);
            failed = true;
            return;
          }
          setOcr(ocrRes);
          setLastRead({ front: f, back: b });
          setBank((prev) => ({ ...prev, bankAccountName: ocrRes.name || prev.bankAccountName }));
        } catch (e: any) {
          const msg = ekycErrorMessage(e, DOCUMENT_FALLBACK_TEXT);
          setOcr(null);
          setLastRead(null);
          setDocError(msg);
          failed = true;
          return;
        }
      }

      try {
        const live = await checkEkycLiveness(fc, f);
        if (isLivenessVerified(live)) {
          setFaceVerified(true);
        } else {
          setFaceError(FACE_FAILED_TEXT);
          failed = true;
        }
      } catch (e: any) {
        const msg = ekycErrorMessage(e, FACE_FALLBACK_TEXT);
        failed = true;
        if (isDocumentRetakeError(msg)) {
          // Máy chủ cần đọc lại giấy tờ: bỏ kết quả cũ để lần sau gọi lại cả hai bước.
          setOcr(null);
          setLastRead(null);
          setDocError(msg);
        } else {
          setFaceError(msg);
        }
      }
    } finally {
      setSubmitting(false);
      if (failed) refreshStatus();
    }
  };

  const finish = () => {
    const blocker = finishBlocker({ faceVerified, ocr, identityOnly, bank });
    if (blocker) {
      message.error(blocker);
      return;
    }
    onVerified({ ocr: ocr as EkycOcrResult, bank });
  };

  const retakeDocument = () => {
    setFront(null);
    setBack(null);
    setOcr(null);
    setLastRead(null);
    setDocError(null);
    setFaceError(null);
    setFaceVerified(false);
    setStep('front');
  };

  const retakeFace = () => {
    setFaceError(null);
    setFaceVerified(false);
    setStep('face');
  };

  const stepTitle: Record<Step, string> = {
    intro: 'Xác thực danh tính (eKYC)',
    front: 'Chụp CCCD — mặt trước',
    back: 'Chụp CCCD — mặt sau',
    face: 'Xác thực khuôn mặt',
    review: 'Xác nhận thông tin',
  };

  // Chưa đồng ý điều khoản dữ liệu → hiện điều khoản trước, chưa cho vào bước chụp eKYC.
  if (open && dataConsent === false) {
    return (
      <PolicyConsentModal
        open
        policyKey="ekyc-data"
        onClose={onClose}
        onAccepted={() => setDataConsent(true)}
      />
    );
  }

  const notice = attemptsLeftNotice(status);
  const locked = isEkycLocked(status) && !faceVerified;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={520}
      centered
      title={locked ? 'Xác thực danh tính (eKYC)' : stepTitle[step]}
      destroyOnClose
    >
      {locked && !submitting ? (
        <EkycLockedNotice />
      ) : noCamera ? (
        /* Không có camera → QR sang mobile (theo REQ: PC không camera) */
        <div className="flex flex-col items-center gap-3 py-4 text-center">
          <AlertTriangle className="text-amber-500" size={28} />
          <p className="text-[15px] font-medium">Thiết bị không có camera</p>
          <p className="text-[13px] text-slate-500">
            Dùng điện thoại quét mã QR dưới đây để tiếp tục xác thực eKYC trên web mobile.
          </p>
          <QRCode value={window.location.href} size={180} />
          <Button onClick={() => setNoCamera(false)} className="!rounded-full mt-2">
            Thử lại camera
          </Button>
        </div>
      ) : step === 'intro' ? (
        <div className="flex flex-col gap-3 py-2">
          <p className="text-[15px]">
            Để tiếp tục, bạn cần xác thực danh tính một lần: chụp <b>CCCD mặt trước</b>,{' '}
            <b>mặt sau</b> và <b>khuôn mặt</b>. Thông tin sẽ được đối chiếu tự động.
          </p>
          <ul className="text-[13px] text-slate-500 list-disc pl-5">
            <li>Chụp nơi đủ sáng, giấy tờ nằm trọn trong khung.</li>
            <li>Khuôn mặt nhìn thẳng, không che.</li>
          </ul>
          {notice && <p className="text-[13px] text-amber-700">{notice}</p>}
          <Button
            type="primary"
            size="large"
            className="!rounded-full mt-2 self-center !px-8"
            style={{ background: BRAND, borderColor: BRAND }}
            loading={!statusLoaded}
            onClick={() => setStep('front')}
          >
            Bắt đầu
          </Button>
        </div>
      ) : step === 'front' ? (
        <CameraCapture
          facingMode="environment"
          shape="card"
          hint="Đưa mặt trước CCCD vào khung"
          onNoCamera={() => setNoCamera(true)}
          onCapture={(blob, url) => {
            setFront({ blob, url });
            setStep('back');
          }}
        />
      ) : step === 'back' ? (
        <CameraCapture
          facingMode="environment"
          shape="card"
          hint="Đưa mặt sau CCCD vào khung"
          onNoCamera={() => setNoCamera(true)}
          onCapture={(blob, url) => {
            setBack({ blob, url });
            setStep('face');
          }}
        />
      ) : step === 'face' ? (
        <CameraCapture
          facingMode="user"
          shape="face"
          hint="Đưa khuôn mặt vào vòng tròn, nhìn thẳng"
          onNoCamera={() => setNoCamera(true)}
          onCapture={(blob) => {
            if (front) runEkyc(front.blob, back?.blob ?? null, blob);
            else retakeDocument();
          }}
        />
      ) : (
        // review
        <div className="flex flex-col gap-3 py-1">
          {submitting ? (
            <div className="flex flex-col items-center gap-3 py-6">
              <Spin />
              <p className="text-[14px] text-slate-500">Đang xác thực giấy tờ & khuôn mặt…</p>
            </div>
          ) : (
            <>
              {ocr?.name ? (
                <div className="rounded-[10px] bg-emerald-50 border border-emerald-200 p-3 flex items-start gap-2">
                  <CheckCircle2 className="text-emerald-600 mt-0.5" size={18} />
                  <div className="text-[14px]">
                    <div>Họ tên: <b>{ocr.name}</b></div>
                    <div>Số CCCD: <b>{ocr.idNumber}</b></div>
                    {ocr.dob && <div>Ngày sinh: {ocr.dob}</div>}
                  </div>
                </div>
              ) : (
                <div className="rounded-[10px] bg-amber-50 border border-amber-200 p-3 text-[14px] text-amber-700">
                  {docError || 'Cab chưa đọc được thông tin trên giấy tờ. Bạn hãy chụp lại căn cước.'}
                </div>
              )}

              {ocr?.name && faceVerified && (
                <div className="rounded-[10px] bg-emerald-50 border border-emerald-200 p-3 flex items-start gap-2 text-[14px]">
                  <CheckCircle2 className="text-emerald-600 mt-0.5" size={18} />
                  <span>Khuôn mặt đã khớp với giấy tờ.</span>
                </div>
              )}

              {ocr?.name && !faceVerified && (
                <div className="rounded-[10px] bg-red-50 border border-red-200 p-3 flex items-start gap-2 text-[14px] text-red-700">
                  <AlertTriangle className="mt-0.5 shrink-0" size={18} />
                  <span>{faceError || FACE_FAILED_TEXT}</span>
                </div>
              )}

              {!faceVerified && notice && <p className="text-[13px] text-amber-700 text-center">{notice}</p>}

              {faceVerified && !identityOnly && (
                <>
                  <div className="text-[14px] font-medium mt-1">Tài khoản nhận tiền</div>
                  <Input
                    className="!rounded-[10px]"
                    placeholder="Tên ngân hàng (VD: Vietcombank)"
                    value={bank.bankName}
                    onChange={(e) => setBank({ ...bank, bankName: e.target.value })}
                  />
                  <Input
                    className="!rounded-[10px]"
                    placeholder="Số tài khoản"
                    value={bank.bankAccountNumber}
                    onChange={(e) => setBank({ ...bank, bankAccountNumber: e.target.value })}
                  />
                  <Input
                    className="!rounded-[10px]"
                    placeholder="Tên chủ tài khoản (phải trùng tên trên CCCD)"
                    value={bank.bankAccountName}
                    onChange={(e) => setBank({ ...bank, bankAccountName: e.target.value })}
                  />
                  <p className="text-[12px] text-slate-400">
                    * Tên chủ tài khoản phải trùng họ tên trên CCCD, nếu không lệnh rút sẽ bị từ chối.
                  </p>
                </>
              )}

              <div className="flex flex-wrap justify-center gap-2 mt-2">
                {faceVerified ? (
                  <Button
                    type="primary"
                    className="!rounded-full !px-6"
                    style={{ background: BRAND, borderColor: BRAND }}
                    onClick={finish}
                  >
                    Hoàn tất
                  </Button>
                ) : (
                  <>
                    <Button icon={<RefreshCw size={15} />} className="!rounded-full" onClick={retakeDocument}>
                      Chụp lại giấy tờ
                    </Button>
                    {ocr?.name && (
                      <Button
                        type="primary"
                        icon={<RefreshCw size={15} />}
                        className="!rounded-full !px-6"
                        style={{ background: BRAND, borderColor: BRAND }}
                        onClick={retakeFace}
                      >
                        Chụp lại khuôn mặt
                      </Button>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default EkycCameraModal;
